import MoreVertIcon from '@mui/icons-material/MoreVert';
import SubdirectoryArrowRight from '@mui/icons-material/SubdirectoryArrowRight';
import {
  Accordion,
  AccordionDetails,
  Box,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { Column } from 'src/components/table/CustomTable.interface';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { lang } from 'src/utils';

import { CustomCellRenderer } from './OOLocationSchedulePageTable';
import {
  CustomTableCellSchedule,
  LocationOverrideChip,
  OverrideEnabledChip,
  OverrideEnabledIcon,
  StyledAccordionSummary,
  StyledExpandMoreIcon,
  StyledTable,
  StyledTableCell,
  TableCellContainer,
} from './OOLocationSchedulePageTable.styled';

interface LocationScheduleAccordionRowProps {
  row: any;
  idx: number;
  columns: Column[];
  expandedColumns?: Column[];
  expandedRow: number | null;
  onToggle: (idx: number) => void;
  onMenuOpen: (
    e: React.MouseEvent<HTMLElement>,
    id: string,
    name: string,
  ) => void;
}

export const LocationScheduleAccordionRow: React.FC<
  LocationScheduleAccordionRowProps
> = ({
  row,
  idx,
  columns,
  expandedColumns,
  expandedRow,
  onToggle,
  onMenuOpen,
}) => {
  const isExpanded = expandedRow === idx;

  return (
    <TableRow key={idx}>
      <TableCellContainer colSpan={columns.length + 2}>
        <Accordion
          disableGutters
          expanded={isExpanded}
          sx={{ p: 0, m: 0 }}
          onChange={() => onToggle(idx)}>
          <StyledAccordionSummary>
            <StyledTable>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{ width: '48px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(idx);
                    }}>
                    <StyledExpandMoreIcon
                      aria-label="expand more"
                      isexpanded={isExpanded}
                    />
                  </TableCell>

                  {columns.map((col, colIdx) => {
                    const isOverrideColumn = col.id === 'override';
                    const isNameColumn = col.id === 'name';
                    const cellValue = row[col.id];

                    return (
                      <CustomTableCellSchedule
                        key={colIdx}
                        align={col.align || 'left'}
                        $isdoublewidth={col.id === 'name'}
                        $isHeader={false}
                        sx={{
                          width: col.width,
                          justifyContent:
                            col.align === 'right' ? 'flex-end' : 'flex-start',
                        }}>
                        {isOverrideColumn && row.latest_override_date ? (
                          <LocationOverrideChip
                            chipName={lang.ownerOperatorOverride}
                          />
                        ) : isNameColumn ? (
                          <CustomTooltip
                            title={cellValue}
                            overflowLineNumber={1}
                            placement="bottom">
                            <Typography>{cellValue}</Typography>
                          </CustomTooltip>
                        ) : (
                          cellValue
                        )}
                      </CustomTableCellSchedule>
                    );
                  })}

                  <StyledTableCell />
                </TableRow>
              </TableBody>
            </StyledTable>
          </StyledAccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ width: '100%' }}>
              <StyledTable size="small">
                {expandedColumns && (
                  <TableHead>
                    <TableRow>
                      <StyledTableCell />
                      {expandedColumns.map((col) => (
                        <TableCell
                          key={col.id}
                          align={(col.align || 'left') as any}
                          sx={{
                            fontWeight: 600,
                            py: 1.5,
                            width: col.width,
                          }}>
                          {col.label}
                        </TableCell>
                      ))}
                      <StyledTableCell />
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {(row.scheduleItems || []).map((item: any, iIdx: number) => {
                    const hasOverrides =
                      Array.isArray(item.overrides) &&
                      item.overrides.length > 0;

                    return (
                      <React.Fragment key={iIdx}>
                        <TableRow>
                          <StyledTableCell />

                          {(expandedColumns ? expandedColumns : columns).map(
                            (col) => (
                              <CustomTableCellSchedule
                                key={col.id}
                                align={col.align || 'left'}
                                $isdoublewidth={col.id === 'name'}
                                $isHeader={false}
                                sx={{ width: col.width }}>
                                {col.id === 'override' ? (
                                  item.latest_override_date ? (
                                    <LocationOverrideChip
                                      chipName={lang.ownerOperatorOverride}
                                    />
                                  ) : item.allow_overrides ? (
                                    <OverrideEnabledChip>
                                      <OverrideEnabledIcon />
                                      {lang.ooOverrideEnabled}
                                    </OverrideEnabledChip>
                                  ) : null
                                ) : col.id === 'latest_override_date' ? (
                                  item.latest_override_date || ''
                                ) : col.id === 'name' &&
                                  CustomCellRenderer?.componentName ? (
                                  CustomCellRenderer.componentName(item, {
                                    id: 'component_name',
                                  })
                                ) : (
                                  item[col.id]
                                )}
                              </CustomTableCellSchedule>
                            ),
                          )}

                          <TableCell align="left" sx={{ width: '48px' }}>
                            {hasOverrides && (
                              <IconButton
                                aria-label="expand component"
                                onClick={(e) =>
                                  onMenuOpen(
                                    e,
                                    item.id,
                                    item.component_name || item.componentName,
                                  )
                                }>
                                <MoreVertIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>

                        {hasOverrides &&
                          item.overrides.map((override: any, oIdx: number) => (
                            <TableRow key={`override-${override.id}-${oIdx}`}>
                              <StyledTableCell />

                              {(expandedColumns
                                ? expandedColumns
                                : columns
                              ).map((col) => (
                                <CustomTableCellSchedule
                                  key={col.id}
                                  align={col.align || 'left'}
                                  $isdoublewidth={col.id === 'name'}
                                  $isHeader={false}
                                  sx={{ py: 1.5, width: col.width }}>
                                  {col.id === 'override' ? (
                                    <LocationOverrideChip
                                      chipName={lang.ownerOperatorOverride}
                                    />
                                  ) : col.id === 'latest_override_date' ? (
                                    override.created_at ?? ''
                                  ) : col.id === 'name' ? (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.5}>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}>
                                        <SubdirectoryArrowRight
                                          fontSize="small"
                                          sx={{ color: 'primary.main' }}
                                        />
                                        {override.component_id
                                          ? CustomCellRenderer?.componentName
                                            ? CustomCellRenderer.componentName(
                                                override,
                                                {
                                                  id: 'component_name',
                                                },
                                              )
                                            : override[col.id] ?? ''
                                          : override.component_name ??
                                            override[col.id] ??
                                            ''}
                                      </Box>
                                    </Box>
                                  ) : (
                                    override[col.id] ?? ''
                                  )}
                                </CustomTableCellSchedule>
                              ))}

                              <StyledTableCell />
                            </TableRow>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </Box>
          </AccordionDetails>
        </Accordion>
      </TableCellContainer>
    </TableRow>
  );
};
